import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types
interface Equipment {
  id: string;
  code: string;
  name: string;
  status: string;
  organization_id: string;
}

interface Vehicle {
  id: string;
  equipment_id: string;
  license_plate: string;
  vin?: string;
  registration_number?: string;
  vehicle_type: 'car' | 'truck' | 'van' | 'motorcycle' | 'heavy_equipment' | 'other';
  fuel_type?: 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'lpg' | 'other';
  transmission?: 'manual' | 'automatic';
  engine_capacity?: number;
  power?: number;
  seats?: number;
  payload_capacity?: number;
  insurance_company?: string;
  insurance_policy_number?: string;
  insurance_expiry?: Date;
  technical_inspection_expiry?: Date;
  registration_expiry?: Date;
  initial_odometer?: number;
  current_odometer?: number;
  odometer_unit?: 'km' | 'miles';
  average_fuel_consumption?: number;
  total_fuel_cost?: number;
  assigned_driver_id?: string;
  assignment_date?: Date;
  created_at: Date;
  updated_at: Date;
  equipment?: Equipment;
}

interface VehicleListParams {
  vehicle_type?: string;
  fuel_type?: string;
  status?: string;
  assigned_driver_id?: string;
  limit?: number;
  offset?: number;
}

interface VehicleListResponse {
  items: Vehicle[];
  total: number;
}

interface VehicleStats {
  total: number;
  byType: Record<string, number>;
  byFuelType: Record<string, number>;
  byStatus: Record<string, number>;
  assigned: number;
  unassigned: number;
  totalOdometer: number;
  averageOdometer: number;
  insuranceExpiringSoon: number;
  technicalExpiringSoon: number;
}

interface ExpiringDocuments {
  insurance: Vehicle[];
  technical: Vehicle[];
  registration: Vehicle[];
}

// Query keys
export const vehicleKeys = {
  all: ['vehicles'] as const,
  lists: () => [...vehicleKeys.all, 'list'] as const,
  list: (filters: VehicleListParams) => [...vehicleKeys.lists(), filters] as const,
  details: () => [...vehicleKeys.all, 'detail'] as const,
  detail: (id: string) => [...vehicleKeys.details(), id] as const,
  stats: () => [...vehicleKeys.all, 'stats'] as const,
  expiringDocuments: (days?: number) =>
    [...vehicleKeys.all, 'expiring-documents', days] as const,
};

// Hooks
export function useVehicles(params: VehicleListParams = {}) {
  return useQuery({
    queryKey: vehicleKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.vehicle_type) searchParams.set('vehicle_type', params.vehicle_type);
      if (params.fuel_type) searchParams.set('fuel_type', params.fuel_type);
      if (params.status) searchParams.set('status', params.status);
      if (params.assigned_driver_id)
        searchParams.set('assigned_driver_id', params.assigned_driver_id);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<VehicleListResponse>(
        `/vehicles?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: vehicleKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ vehicle: Vehicle }>(
        `/vehicles/${id}`
      );
      return response.data.vehicle;
    },
    enabled: !!id,
  });
}

export function useVehicleStats() {
  return useQuery({
    queryKey: vehicleKeys.stats(),
    queryFn: async () => {
      const response = await apiClient.get<{ stats: VehicleStats }>(
        '/vehicles/stats'
      );
      return response.data.stats;
    },
  });
}

export function useExpiringDocuments(days: number = 30) {
  return useQuery({
    queryKey: vehicleKeys.expiringDocuments(days),
    queryFn: async () => {
      const response = await apiClient.get<ExpiringDocuments>(
        `/vehicles/expiring-documents?days=${days}`
      );
      return response.data;
    },
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Vehicle>) => {
      const response = await apiClient.post<{ vehicle: Vehicle }>(
        '/vehicles',
        data
      );
      return response.data.vehicle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Vehicle> }) => {
      const response = await apiClient.put<{ vehicle: Vehicle }>(
        `/vehicles/${id}`,
        data
      );
      return response.data.vehicle;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}

export function useDeleteVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/vehicles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}

export function useUpdateOdometer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reading }: { id: string; reading: number }) => {
      const response = await apiClient.post<{ vehicle: Vehicle }>(
        `/vehicles/${id}/odometer`,
        { reading }
      );
      return response.data.vehicle;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}

export function useAssignDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, driver_id }: { id: string; driver_id: string }) => {
      const response = await apiClient.post<{ vehicle: Vehicle }>(
        `/vehicles/${id}/assign-driver`,
        { driver_id }
      );
      return response.data.vehicle;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}

export function useUnassignDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ vehicle: Vehicle }>(
        `/vehicles/${id}/unassign-driver`
      );
      return response.data.vehicle;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.stats() });
    },
  });
}
